import React, { useEffect } from 'react';
import { Heart, Shield, Award, MapPin } from 'lucide-react';
import { companyInfo, values, images } from '../data/mock';

const iconMap = {
  Heart: Heart,
  Shield: Shield,
  Award: Award
};

const AboutPage = () => {
  useEffect(() => {
    // SEO: Dynamic page title and meta description
    document.title = 'Über uns | Helper-Schweiz - Ihr Entrümpelungsexperte in der Deutschschweiz';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Lernen Sie Christian Reinhardt kennen - Ihr persönlicher Partner für Entrümpelung und Haushaltsauflösung in der Deutschschweiz. Professionell, zuverlässig und fair. ☎ +41 77 252 38 20');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#f0f5fa] to-[#e8f0f8] py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e3a5f]">Über Helper-Schweiz</h1>
          <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
            {companyInfo.owner} - Ihr persönlicher Partner für Entrümpelung und Haushaltsauflösung
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">Meine Geschichte</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Mein Name ist {companyInfo.owner} und ich bin der Inhaber von Helper-Schweiz. Als Einzelunternehmer im Nebenerwerb habe ich mich auf professionelle Entrümpelungen spezialisiert.
                </p>
                <p>
                  Mein Fokus liegt auf <strong>kleinen bis mittleren Entrümpelungen</strong> – von einzelnen Räumen über Keller und Estrich bis hin zu kleineren Wohnungen. Diese Grösse erlaubt es mir, jeden Auftrag persönlich und mit grösster Sorgfalt durchzuführen.
                </p>
                <p>
                  Für <strong>grössere Projekte</strong> wie umfangreiche Haushaltsauflösungen oder grosse Liegenschaften stehe ich gerne auf Anfrage zur Verfügung. Dabei prüfe ich im Vorfeld, ob ich Ihrem Projekt gerecht werden kann und organisiere bei Bedarf zuverlässige Unterstützung.
                </p>
                <p>
                  Ich verstehe, dass jede Entrümpelung ihre eigene Geschichte hat – sei es eine Haushaltsauflösung, eine Nachlassregelung oder einfach die Notwendigkeit, Ordnung zu schaffen. Mit Respekt, Diskretion und Professionalität gehe ich jede Aufgabe an.
                </p>
                <p>
                  Durch meine langjährige handwerkliche Erfahrung im Innenbereich weiss ich genau, worauf es bei einer professionellen Entrümpelung und einer sauberen Übergabe ankommt.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={images.professional}
                alt="Professionelle Entrümpelung"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-4">Meine Werte</h2>
          <p className="text-gray-600 text-center mb-12">Was mich auszeichnet und antreibt</p>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => {
              const IconComponent = iconMap[value.icon];
              return (
                <div key={value.id} className="bg-white rounded-2xl p-8 shadow-md text-center">
                  <div className="w-16 h-16 bg-[#f0f5fa] rounded-full flex items-center justify-center mx-auto mb-4">
                    {IconComponent && <IconComponent className="w-8 h-8 text-[#c41e3a]" />}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Region Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#1e3a5f] text-center mb-8">Mein Einzugsgebiet</h2>
          <div className="bg-[#f0f5fa] rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <div>
                <p className="text-gray-700 leading-relaxed">
                  Ich bin hauptsächlich im <strong>Kanton Thurgau</strong> tätig und kenne die Region wie meine Westentasche. Hier kann ich schnell und flexibel reagieren.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Auf Anfrage</strong> bin ich auch in der ganzen Deutschschweiz für Sie im Einsatz. Für Aufträge ausserhalb des Kantons Thurgau kontaktieren Sie mich bitte im Vorfeld, damit ich Ihnen ein individuelles Angebot erstellen kann.
                </p>
                <p className="text-[#1e3a5f] font-semibold mt-6">
                  Standort: {companyInfo.address}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Single Company Section */}
      <section className="py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-8">Warum eine Einzelfirma?</h2>
          <div className="space-y-6 text-blue-100 text-lg leading-relaxed">
            <p>
              Als Einzelunternehmer kann ich Ihnen einen <strong className="text-white">persönlichen Service</strong> bieten, den grössere Firmen oft nicht leisten können. Sie haben von Anfang bis Ende denselben Ansprechpartner – nämlich mich.
            </p>
            <p>
              Jeder Auftrag wird von mir persönlich durchgeführt, was Ihnen <strong className="text-white">höchste Qualität und Zuverlässigkeit</strong> garantiert. Bei mir wissen Sie genau, wer bei Ihnen arbeitet.
            </p>
            <p>
              Durch meine Spezialisierung auf <strong className="text-white">kleine bis mittlere Projekte</strong> kann ich jedem Auftrag die nötige Zeit und Aufmerksamkeit widmen, die er verdient.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
